import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { DollarSign, Target, TrendingUp, Trophy, Receipt } from 'lucide-react';
import { getPlatformLogo, getPlatformName } from './types';
import { ConversionModal } from './modals/ConversionModal';
import { ConversionRateModal } from './modals/ConversionRateModal';
import { AverageTicketModal } from './modals/AverageTicketModal';
import { RevenueModal } from './modals/RevenueModal';

interface PlatformMetrics {
  id: string;
  name: string;
  data: {
    conversions: number;
    revenue: number;
    conversionRate: number;
  };
}

interface PerformanceHighlightsProps {
  platforms: PlatformMetrics[];
}

interface HighlightCard {
  modalType: 'conversions' | 'rate' | 'ticket' | 'revenue';
  metricLabel: string;
  icon: React.ReactNode;
  platformId: string;
  platformName: string;
  value: string;
  iconColor: string;
}

export function PerformanceHighlights({ platforms }: PerformanceHighlightsProps) {
  useLanguage();
  const [openModal, setOpenModal] = useState<'conversions' | 'rate' | 'ticket' | 'revenue' | null>(null);
  
  // Filter out platforms with no data
  const validPlatforms = platforms.filter(p => 
    p.data.conversions > 0 || p.data.revenue > 0
  );

  if (validPlatforms.length === 0) {
    return null;
  }

  // Find winners for each metric
  const highestRevenue = [...validPlatforms].sort((a, b) => b.data.revenue - a.data.revenue)[0];
  const highestConversions = [...validPlatforms].sort((a, b) => b.data.conversions - a.data.conversions)[0];
  const highestRate = [...validPlatforms].sort((a, b) => b.data.conversionRate - a.data.conversionRate)[0];
  
  // Calculate highest average ticket
  const platformsWithTicket = validPlatforms.map(p => ({
    ...p,
    averageTicket: p.data.conversions > 0 ? p.data.revenue / p.data.conversions : 0
  }));
  const highestTicket = [...platformsWithTicket].sort((a, b) => b.averageTicket - a.averageTicket)[0];

  const highlights: HighlightCard[] = [
    {
      modalType: 'conversions',
      metricLabel: translateInterfaceText("Maior Conversão Total"),
      icon: <Target className="h-4 w-4" />,
      platformId: highestConversions.id,
      platformName: highestConversions.name,
      value: highestConversions.data.conversions.toString(),
      iconColor: 'text-[#2563EB]'
    },
    {
      modalType: 'rate',
      metricLabel: translateInterfaceText("Maior Taxa Média"),
      icon: <TrendingUp className="h-4 w-4" />,
      platformId: highestRate.id,
      platformName: highestRate.name,
      value: `${highestRate.data.conversionRate.toFixed(2)}%`,
      iconColor: 'text-[#2563EB]'
    },
    {
      modalType: 'ticket',
      metricLabel: translateInterfaceText("Maior Ticket Médio"),
      icon: <Receipt className="h-4 w-4" />,
      platformId: highestTicket.id,
      platformName: highestTicket.name,
      value: `R$ ${highestTicket.averageTicket.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      iconColor: 'text-[#2563EB]'
    },
    {
      modalType: 'revenue',
      metricLabel: translateInterfaceText('Maior Receita'),
      icon: <DollarSign className="h-4 w-4" />,
      platformId: highestRevenue.id,
      platformName: highestRevenue.name,
      value: `R$ ${highestRevenue.data.revenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      iconColor: 'text-[#2563EB]'
    }
  ];

  return (
    <div className="w-full mt-10">
      {/* Separador Premium */}
      <div className="h-px bg-[#F2F4F7] mb-6" />
      
      {/* Section Header - Alinhado à esquerda */}
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="h-5 w-5 text-[#2563EB]" />
        <h3 className="text-[18px] font-semibold text-[#2D3748]">{translateInterfaceText("Destaques de Performance")}</h3>
      </div>
      
      <div className="grid grid-cols-1 gap-6 w-full md:grid-cols-2 lg:grid-cols-4">
        {highlights.map((highlight, index) => {
          const modalType = highlight.modalType;
          
          return (
            <div
              key={index}
              onClick={() => setOpenModal(modalType)}
              className="bg-white border-2 border-[#E5E7EB] rounded-[12px] shadow-[0_2px_6px_rgba(0,0,0,0.03)] hover:shadow-[0_2px_8px_rgba(110,74,239,0.15)] transition-all duration-200 min-h-[120px] p-6 flex flex-col justify-between cursor-pointer"
            >
              {/* Título com ícone pequeno no canto */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[13px] font-semibold text-[#6B7280] uppercase tracking-wide">
                  {highlight.metricLabel}
                </h4>
                <span className="text-[#2563EB] opacity-60">
                  {highlight.icon}
                </span>
              </div>
              
              {/* Valor em destaque */}
              <p className="text-[28px] font-semibold text-[#111827] mb-1">
                {highlight.value}
              </p>
              
              {/* Subtexto */}
              <p className="text-[13px] text-[#6B7785]">
                {highlight.platformName} {" "}{translateInterfaceText("ficou no topo")}</p>
            </div>
          );
        })}
      </div>
      
      {/* Modals */}
      <ConversionModal
        open={openModal === 'conversions'}
        onOpenChange={(open) => !open && setOpenModal(null)}
        conversions={highestConversions.data.conversions}
        revenue={highestConversions.data.revenue}
        conversionRate={highestConversions.data.conversionRate}
        averageTicket={highestConversions.data.conversions > 0 ? highestConversions.data.revenue / highestConversions.data.conversions : 0}
      />
      <ConversionRateModal
        open={openModal === 'rate'}
        onOpenChange={(open) => !open && setOpenModal(null)}
        conversions={highestRate.data.conversions}
        revenue={highestRate.data.revenue}
        conversionRate={highestRate.data.conversionRate}
        averageTicket={highestRate.data.conversions > 0 ? highestRate.data.revenue / highestRate.data.conversions : 0}
      />
      <AverageTicketModal
        open={openModal === 'ticket'}
        onOpenChange={(open) => !open && setOpenModal(null)}
        conversions={highestTicket.data.conversions}
        revenue={highestTicket.data.revenue}
        conversionRate={highestTicket.data.conversionRate}
        averageTicket={highestTicket.averageTicket}
      />
      <RevenueModal
        open={openModal === 'revenue'}
        onOpenChange={(open) => !open && setOpenModal(null)}
        conversions={highestRevenue.data.conversions}
        revenue={highestRevenue.data.revenue}
        conversionRate={highestRevenue.data.conversionRate}
        averageTicket={highestRevenue.data.conversions > 0 ? highestRevenue.data.revenue / highestRevenue.data.conversions : 0}
      />
    </div>
  );
}
